import { useEffect, useMemo, useState } from "react";
import { useTags } from "../context/TagsContext";
import { createTag, updateTag, deleteTag } from "../api/tags";
import "./TagsPage.css";

const EMPTY_FORM = {
  label: "",
  name: "",
  type: "Basic",
  description: "",
  defaultCheckmark: false,
};

export default function TagsPage() {
  const { tags, loading, reload } = useTags();
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [mode, setMode] = useState("view");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const filteredTags = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tags;
    return tags.filter((t) =>
      (t.label || "").toLowerCase().includes(q) ||
      (t.name || "").toLowerCase().includes(q)
    );
  }, [tags, search]);

  useEffect(() => {
    if (!selectedId && tags.length > 0) {
      setSelectedId(tags[0].id);
    }
  }, [tags, selectedId]);

  useEffect(() => {
    if (mode === "new") {
      setForm(EMPTY_FORM);
      return;
    }

    const selected = tags.find((t) => t.id === selectedId);
    if (!selected) return;

    setForm({
      label: selected.label || "",
      name: selected.name || "",
      type: selected.type || "Basic",
      description: selected.description || "",
      defaultCheckmark: !!selected.defaultCheckmark,
    });
  }, [selectedId, tags, mode]);

  const handleSelect = (tagId) => {
    setMode("view");
    setSelectedId(tagId);
    setError("");
  };

  const handleNew = () => {
    setMode("new");
    setSelectedId(null);
    setError("");
    setForm(EMPTY_FORM);
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.label.trim()) {
      setError("Label ist Pflicht");
      return;
    }

    setSaving(true);
    setError("");
    try {
      if (mode === "new") {
        await createTag({
          label: form.label.trim(),
          name: form.name.trim() || undefined,
          type: form.type,
          description: form.description.trim(),
          defaultCheckmark: !!form.defaultCheckmark,
        });
        await reload();
        setMode("view");
      } else if (selectedId) {
        await updateTag(selectedId, {
          label: form.label.trim(),
          name: form.name.trim() || undefined,
          type: form.type,
          description: form.description.trim(),
          defaultCheckmark: !!form.defaultCheckmark,
        });
        await reload();
      }
    } catch (e) {
      setError("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    if (!window.confirm("Tag wirklich loeschen?")) return;

    setSaving(true);
    setError("");
    try {
      await deleteTag(selectedId);
      await reload();
      setSelectedId(null);
      setForm(EMPTY_FORM);
    } catch (e) {
      setError("Loeschen fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tags-page">
      <div className="tags-list-panel">
        <div className="tags-list-header">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tags suchen..."
          />
          <button type="button" onClick={handleNew}>
            Neuer Tag
          </button>
        </div>

        <div className="tags-list">
          {loading && <div className="tags-list-empty">Lade Tags...</div>}
          {!loading && filteredTags.length === 0 && (
            <div className="tags-list-empty">Keine Tags</div>
          )}
          {!loading &&
            filteredTags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                className={`tags-list-item ${tag.id === selectedId ? "active" : ""}`}
                onClick={() => handleSelect(tag.id)}
              >
                <div className="tags-list-item-title">{tag.label || tag.name}</div>
                <div className="tags-list-item-sub">{tag.type || "Basic"}</div>
              </button>
            ))}
        </div>
      </div>

      <div className="tags-editor">
        <div className="tags-editor-header">
          <h2>{mode === "new" ? "Tag erstellen" : "Tag bearbeiten"}</h2>
          {error && <span className="tags-error">{error}</span>}
        </div>

        <div className="tags-editor-form">
          <label>Label</label>
          <input
            type="text"
            value={form.label}
            onChange={handleChange("label")}
          />

          <label>Name (optional)</label>
          <input
            type="text"
            value={form.name}
            onChange={handleChange("name")}
          />

          <label>Typ</label>
          <div className="tags-type-row">
            <button
              type="button"
              className={form.type === "Basic" ? "active" : ""}
              onClick={() => setForm((prev) => ({ ...prev, type: "Basic" }))}
            >
              Basic
            </button>
            <button
              type="button"
              className={form.type === "Checkmark" ? "active" : ""}
              onClick={() => setForm((prev) => ({ ...prev, type: "Checkmark" }))}
            >
              Checkmark
            </button>
          </div>

          {form.type === "Checkmark" && (
            <label className="tags-checkbox">
              <input
                type="checkbox"
                checked={form.defaultCheckmark}
                onChange={handleChange("defaultCheckmark")}
              />
              Standardmaessig aktiv
            </label>
          )}

          <label>Beschreibung</label>
          <textarea
            rows={4}
            value={form.description}
            onChange={handleChange("description")}
          />
        </div>

        <div className="tags-editor-actions">
          {mode !== "new" && (
            <button type="button" className="danger" onClick={handleDelete} disabled={saving}>
              Tag loeschen
            </button>
          )}
          <button type="button" className="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Speichern..." : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}
